import styles from './signalr-context.module.scss';
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import * as SignalR from '@microsoft/signalr';
import { CheckboxLabel, ErrorTypesEnum, IWebkuaSignalRResponse, IWebkuaSignalRStatusResponse, OrderTypesEnum, SnackBarContext } from '@frontend-workspace/react-shared-lib';
import { ISignalR } from '../_models/signal-r';
import { useTranslation } from 'react-i18next';
import { useMakeRequestHook } from '../requestUtils';

/* eslint-disable-next-line */
export interface SignalrContextProps { }

const SignalRContext = React.createContext<SignalR.HubConnection | undefined>(undefined);
const SetSignalRContext = React.createContext<any | undefined>(undefined);

function SignalRProvider({ children }: { children: ReactNode }) {
  const makeRequest = useMakeRequestHook();
  const { t } = useTranslation();
  const [connection, setConnection] = useState<SignalR.HubConnection | undefined>(undefined);
  const { setSnackbarMessage } = useContext(SnackBarContext);

  const connectToSignalR = () => {
    if (!connection || connection.state === SignalR.HubConnectionState.Disconnected) {
      makeRequest(`signalr/negotiate/webkuachanges`, 'POST')
        .then((result: ISignalR) => {
          const connection = new SignalR.HubConnectionBuilder().withUrl(result.url, {
            accessTokenFactory: () => result.accessToken
          })
            .withAutomaticReconnect()
            .build();

          connection.start().then(() => setConnection(connection));
        })
        .catch(error => {
          console.log(error);
          setSnackbarMessage({ content: t("signalRContext.failedToConnectToSignalR"), type: "error" });
        });
    } else {
      const msInfiveMinutes = 1000 * 60 * 5;
      setTimeout(() => {
        connectToSignalR();
      }, msInfiveMinutes);
    }
  };

  useEffect(() => {
    connectToSignalR();
  }, [connection]);
  return (
    <SignalRContext.Provider value={connection}>
      <SetSignalRContext.Provider value={setConnection}>
        {children}
      </SetSignalRContext.Provider>
    </SignalRContext.Provider>
  );
}

function UseSignalRHook(handler: (response: IWebkuaSignalRResponse) => void, types: Array<OrderTypesEnum>, onError?: () => void) {
  const { t } = useTranslation();
  const connection = useContext(SignalRContext);
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const refreshHandler = (res: any) => {
    if (types.includes(res.OrderType)) {
      const errorType = ErrorTypesEnum[res.OrderType as keyof typeof ErrorTypesEnum];
      setSnackbarMessage({ content: t("signalRContext.actionExecuted") + ': ' + errorType, type: "success", userId: res.UserId });
      handler(res);
    }
  };

  const errorHandler = (type: IWebkuaSignalRResponse) => {
    setSnackbarMessage({
      content: <>
        <div>{ErrorTypesEnum['Base'] + (OrderTypesEnum[type.OrderType as unknown as keyof typeof OrderTypesEnum] || type.OrderType)}</div>
        <div>{`${t("signalRContext.step")}: ${type.StepName}`}</div>
        <div>{`${t("standard.errorText")}: ${type.ErrorText}`}</div>
      </>, type: "error"
    });
    if (onError && types.includes(type.OrderType)) {
      onError();
    }
  };
  const statusHandler = (type: IWebkuaSignalRStatusResponse) => {
    setSnackbarMessage({
      content: <>
        <p>Behandler ordre: firmadetaljer</p>
        <CheckboxLabel checked={type.StepNumber > 0} label={t("signalRContext.infoFetched")} />
        <CheckboxLabel checked={type.StepNumber > 1} label={t("signalRContext.orderValidated")} />
        <CheckboxLabel checked={type.StepNumber > 2} label={t("signalRContext.userRightsValidated")} />
        <CheckboxLabel checked={type.StepNumber > 3} label={"SuperOffice"} />
        <CheckboxLabel checked={type.StepNumber > 4} label={"Nimbus"} />
        <CheckboxLabel checked={type.StepNumber > 5} label={"WebKua"} />
        <CheckboxLabel checked={type.StepNumber > 6} label={"Visma"} />
        <CheckboxLabel checked={type.StepNumber > 7} label={"AD"} />
      </>, type: "info"
    });
  };
  useEffect(() => {
    connection?.on("refresh", refreshHandler);
    connection?.on("error", errorHandler);
    connection?.on("status", statusHandler);
    return () => {
      connection?.off("refresh", refreshHandler);
      connection?.off("error", errorHandler);
      connection?.off("status", statusHandler);
    }
  }, [connection]);
}

export { SignalRProvider, SignalRContext, UseSignalRHook }